import { formatDistanceToNow, formatISO, isValid, parseISO } from "date-fns";

const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

type DateInput = string | number | Date;

function toDate(date: DateInput): Date {
  return typeof date === "string" ? parseISO(date) : new Date(date);
}

// Extracts the wall-clock components of `date` as rendered in `tz`, so we can
// assemble them into the exact "HH:mm DD/MM/YYYY"-style strings the app has
// always used (replaces moment.utc(date).tz(tz).format(...)).
function partsInTimezone(date: Date) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";

  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour") === "24" ? "00" : get("hour"),
    minute: get("minute"),
  };
}

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

// Monday-start week bounds (matches the one call site - the old
// notIncServiceWeekly filter on the Dashboard - that got this right).
function startOfWeek(date: Date): Date {
  const d = startOfDay(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d;
}

function endOfWeek(date: Date): Date {
  const end = startOfWeek(date);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
}

export interface DateCountPoint {
  time_created: number;
  count: number;
}

export const dateUtils = {
  formatDate: (date: DateInput): string => {
    const d = toDate(date);
    if (!isValid(d)) return "N/A";
    const p = partsInTimezone(d);
    return `${p.hour}:${p.minute} ${p.day}/${p.month}/${p.year}`;
  },

  formatDateTimeOnly: (date: DateInput): string => {
    const d = toDate(date);
    if (!isValid(d)) return "N/A";
    const p = partsInTimezone(d);
    return `${p.hour}:${p.minute}`;
  },

  formatDateDateOnly: (date: DateInput): string => {
    const d = toDate(date);
    if (!isValid(d)) return "N/A";
    const p = partsInTimezone(d);
    return `${p.day}/${p.month}/${p.year}`;
  },

  formatDateMonthYearOnly: (date: DateInput): string => {
    const d = toDate(date);
    if (!isValid(d)) return "N/A";
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: tz,
      month: "short",
      year: "numeric",
    }).format(d);
  },

  getTimezone: (): string => tz,

  isValidDate: (date: DateInput): boolean => isValid(toDate(date)),

  formatRelative: (date: DateInput): string =>
    formatDistanceToNow(toDate(date), { addSuffix: true }),

  formatISO: (date: DateInput): string => formatISO(toDate(date)),

  /** Same calendar day as now (local time). */
  isToday: (date: DateInput): boolean => {
    const d = toDate(date);
    const now = new Date();
    return (
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    );
  },

  /** Within the current Monday-Sunday week (local time). */
  isThisWeek: (date: DateInput): boolean => {
    const d = toDate(date);
    const now = new Date();
    return d >= startOfWeek(now) && d <= endOfWeek(now);
  },

  /** Same calendar month as now (local time). */
  isThisMonth: (date: DateInput): boolean => {
    const d = toDate(date);
    const now = new Date();
    return (
      d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
    );
  },

  /** Within the last `days` * 24h, relative to now. */
  isWithinLastDays: (date: DateInput, days: number): boolean => {
    const d = toDate(date);
    return d.getTime() > Date.now() - days * 24 * 60 * 60 * 1000;
  },

  /**
   * Buckets `items` by local calendar day, summing to a count per day and
   * returning points sorted ascending by day - the shape every "New X over
   * time" area chart in the app renders.
   */
  groupByDay: <T,>(
    items: T[],
    getDate: (item: T) => DateInput,
  ): DateCountPoint[] => {
    const grouped = items.reduce<Record<string, number>>((acc, item) => {
      const d = toDate(getDate(item));
      const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const key = day.getTime();
      acc[key] = (acc[key] ?? 0) + 1;
      return acc;
    }, {});

    return Object.keys(grouped)
      .map((key) => ({
        time_created: Number(key),
        count: grouped[key] as number,
      }))
      .sort((a, b) => a.time_created - b.time_created);
  },

  /**
   * Running total per month of the current year, up to the current month -
   * the shape every "Total X added this year" chart renders.
   */
  monthlyRunningTotal: <T,>(
    items: T[],
    getDate: (item: T) => DateInput,
  ): DateCountPoint[] => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    const points: DateCountPoint[] = [];
    let runningTotal = 0;

    for (let month = 0; month <= currentMonth; month++) {
      const monthTotal = items.filter((item) => {
        const d = toDate(getDate(item));
        return d.getMonth() === month && d.getFullYear() === currentYear;
      }).length;
      runningTotal += monthTotal;
      points.push({
        time_created: new Date(currentYear, month).getTime(),
        count: runningTotal,
      });
    }

    return points;
  },
};
